import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';

const WhatsAppVisio = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  const features = [
    {
      icon: 'ri-video-line',
      title: 'Diagnostic Visuel',
      description: 'Montrez-nous votre problème en direct pour un diagnostic précis'
    },
    {
      icon: 'ri-time-line',
      title: 'Gain de Temps',
      description: 'Évitez un déplacement inutile, nous évaluons la situation à distance'
    },
    {
      icon: 'ri-money-euro-circle-line',
      title: 'Devis Immédiat',
      description: 'Recevez une estimation précise après la visio-consultation'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Prise de RDV',
      description: 'Planifiez l\'intervention directement après la visio si nécessaire'
    }
  ];

  const handleWhatsAppCall = () => {
    window.open('https://wa.me/33554542545?text=Bonjour,%20je%20souhaite%20une%20visio-consultation%20pour%20un%20diagnostic', '_blank');
  };

  return (
    <section ref={ref} className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-teal-50 via-white to-orange-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-teal-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/9ebf51d95b086abf065343753c3eafb5.jpg"
                alt="Visio-consultation WhatsApp pour diagnostic fenêtres"
                className="w-full h-full object-cover object-top"
              />
              
              {/* Floating WhatsApp Badge */}
              <div className="absolute top-4 md:top-6 left-4 md:left-6 bg-white rounded-2xl shadow-xl p-3 md:p-4 flex items-center space-x-2 md:space-x-3">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center">
                  <i className="ri-whatsapp-line text-xl md:text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-medium">Disponible sur</div>
                  <div className="text-sm font-bold text-gray-900">WhatsApp</div>
                </div>
              </div>

              {/* Floating Stats Card */}
              <div className="absolute bottom-4 md:bottom-6 right-4 md:right-6 bg-white rounded-2xl shadow-xl p-3 md:p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <i className="ri-video-chat-line text-xl md:text-2xl text-teal-600"></i>
                  <span className="text-xl md:text-2xl font-bold text-gray-900">500+</span>
                </div>
                <div className="text-xs text-gray-600 font-medium">Visios réalisées</div>
              </div>
            </div>

            {/* Decorative element */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full opacity-20 blur-2xl"></div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-video-line mr-2"></i>
              Nouveau Service
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              <strong className="text-teal-600">Visio-Consultation</strong> par WhatsApp
            </h2>

            <p className="text-lg md:text-xl text-gray-600 mb-6 md:mb-8 leading-relaxed">
              Bénéficiez d'un <strong>diagnostic à distance</strong> en quelques minutes ! Montrez-nous votre problème de fenêtre, baie vitrée ou volet roulant via WhatsApp et obtenez un <strong>devis immédiat</strong>.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-8 md:mb-10">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="flex items-start space-x-3 bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 text-sm">{feature.title}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <button
                onClick={handleWhatsAppCall}
                className="inline-flex items-center justify-center bg-gradient-to-r from-green-500 to-green-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl md:text-2xl mr-2 md:mr-3"></i>
                Démarrer une Visio
              </button>
              
              <a
                href="tel:0554542545"
                className="inline-flex items-center justify-center bg-white text-gray-900 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:bg-gray-50 transition-all shadow-md hover:shadow-lg border-2 border-gray-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-lg md:text-xl mr-2 md:mr-3"></i>
                05 54 54 25 45
              </a>
            </div>

            <p className="text-sm text-gray-500 mt-4 md:mt-6 flex items-center">
              <i className="ri-shield-check-line text-teal-600 mr-2"></i>
              Service gratuit et sans engagement
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhatsAppVisio;
